import { useTheme } from '@emotion/react';
import { faAngleDown, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useMediaQuery } from '@mui/material';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { selectConfigurationParameterById } from '!core/redux';
import { Icon, TrlPopoverSubMenu } from '»components/atoms';
import { ButtonMain } from '»components/atoms/Buttons';
import {
  legalStatusListSelector,
  onboardingDocumentsSelector,
} from '»components/Auth/Onboarding/redux/onboarding.selectors';
import { TrlNoAuthMenu } from '»components/molecules';
import { questionnaireSlice } from '»components/Questionnaire/redux';
import {
  isAureaActiveClient,
  isFinantechActiveClient,
  isHemeraActiveClient,
  isStinvestActiveClient,
} from '»utils/clientsUtils';
import {
  useAppBarContext,
  useCurrentPage,
  usePageNavigation,
} from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import {
  authSlice,
  GeneralConditions,
  loginSlice,
  onboardingSlice,
} from '../../../Auth';
import {
  AppBarLeftStyled,
  AppBarOuterWrapperStyled,
  AppBarRightStyled,
  AppBarWrapperStyled,
  GoBackArrow,
  OnboardingButtonContainer,
} from '../AppBar.styles';

export const AppBarPublic = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();

  const appBarRef = useRef(null);
  const onboardingButtonRef = useRef(null);

  const [onboardingPopoverAnchor, setOnboardingPopoverAnchor] = useState(null);

  const { setBarHeight } = useAppBarContext();
  const { navigateToPage } = usePageNavigation();

  const documents = useSelector(onboardingDocumentsSelector);
  const hasOnboarding =
    useSelector((state) =>
      selectConfigurationParameterById(state, 'ONBOARDING'),
    )?.value === 'Y';
  const legalStatusList = useSelector(legalStatusListSelector);

  const {
    isLandingPage,
    isNoAuthFormPage,
    isOnboardingMainFormStep,
    isOnboardingAdvancedSteps,
    isLoginValidationStep,
    isOnboardingDocumentsAttach,
    isOnboardingCreateAccountStep,
  } = useCurrentPage(location.pathname);

  // eslint-disable-next-line no-undef
  const myConfig = config();
  const { showTermsAndConditions } = myConfig;

  const alertMessage = TrlFormattedMessagePlain({
    id: 'TRLT_UI-LEAVE_PAGE_ALERT',
  });

  const NEW_ACCOUNT_EXP = isAureaActiveClient
    ? 'TRLT_UI-OPEN_ACCOUNT'
    : 'TRLT_UI-CREATE_ACCOUNT';

  const LOGIN_EXP = isAureaActiveClient
    ? 'TRLT_UI-ACCESS_ACCOUNT'
    : 'TRLT_UI-LOGIN';

  useLayoutEffect(() => {
    setBarHeight(appBarRef.current.offsetHeight);
  }, [setBarHeight]);

  const handleOpenOnboardingPopover = (event) => {
    setOnboardingPopoverAnchor(event.currentTarget);
  };

  const handleCloseOnboardingPopover = () => {
    setOnboardingPopoverAnchor(null);
  };

  const handleGoBack = (event) => {
    if (isOnboardingAdvancedSteps) {
      if (isOnboardingMainFormStep) {
        const confirmLeave = window.confirm(alertMessage);
        if (!confirmLeave) {
          event.preventDefault();
          return;
        }

        dispatch(authSlice.actions.resetAuth());
        dispatch(questionnaireSlice.actions.clearQuestionnaire());
      } else {
        if (isOnboardingDocumentsAttach) {
          const contractIndex = documents.findIndex(
            (doc) => doc.isFinancialContract === 'S',
          );
          dispatch(
            onboardingSlice.actions.removeDocument({ index: contractIndex }),
          );
        }
        dispatch(
          onboardingSlice.actions.setOnboardingCurrentStep({
            direction: 'backward',
          }),
        );
      }
    } else if (isStinvestActiveClient && isOnboardingCreateAccountStep) {
      dispatch(
        onboardingSlice.actions.setOnboardingCurrentStep({
          direction: 'backward',
        }),
      );
    } else if (isLoginValidationStep) {
      dispatch(
        loginSlice.actions.setLoginCurrentStep({
          direction: 'backward',
        }),
      );
    } else {
      navigateToPage({
        route: '/',
        menuItem: 'LANDING_PAGE',
        shouldResetAuth: true,
        shouldResetQuest: true,
      });
    }
  };

  return (
    <AppBarOuterWrapperStyled
      isFormPage={isNoAuthFormPage}
      isOnboardingAdvancedSteps={isOnboardingAdvancedSteps}
      isMobile={isMobile}
    >
      <GoBackArrow
        icon={faChevronLeft}
        onClick={handleGoBack}
        isFormPage={isNoAuthFormPage}
      />
      <AppBarWrapperStyled
        ref={appBarRef}
        isFormPage={isNoAuthFormPage}
        isOnboardingAdvancedSteps={isOnboardingAdvancedSteps}
      >
        <AppBarLeftStyled>
          <Icon
            style={{
              width: 'auto',
              height: isMobile ? '35px' : '46px',
              cursor: 'pointer',
            }}
            iconI={
              (isMobile || isNoAuthFormPage) &&
              !isFinantechActiveClient &&
              !isHemeraActiveClient
                ? theme.customLogo.nlogo
                : theme.customLogo.logo
            }
            onClick={(event) => {
              if (isOnboardingAdvancedSteps) {
                const confirmLeave = window.confirm(alertMessage);
                if (!confirmLeave) {
                  event.preventDefault();
                  return;
                }

                dispatch(authSlice.actions.resetAuth());
                dispatch(questionnaireSlice.actions.clearQuestionnaire());
              }

              navigateToPage({
                route: '/',
                menuItem: 'LANDING_PAGE',
                shouldResetAuth: true,
                shouldResetQuest: true,
              });
            }}
          />
        </AppBarLeftStyled>
        <AppBarRightStyled
          hide={(isMobile && !isLandingPage) || (!isMobile && isNoAuthFormPage)}
          isMobile={isMobile}
        >
          {isMobile ? (
            <TrlNoAuthMenu />
          ) : (
            <>
              {hasOnboarding && (
                <OnboardingButtonContainer
                  isSubMenuOpen={Boolean(onboardingPopoverAnchor)}
                >
                  <ButtonMain
                    buttonRef={onboardingButtonRef}
                    id="onboardingButton"
                    onClick={(event) => {
                      if (isStinvestActiveClient) {
                        handleOpenOnboardingPopover(event);
                      } else {
                        navigateToPage({
                          route: '/Onboarding',
                          menuItem: 'ONBOARDING',
                        });
                      }
                    }}
                    variant="secondary"
                    expressionId={NEW_ACCOUNT_EXP}
                    icon={isStinvestActiveClient && faAngleDown}
                  />
                  {isStinvestActiveClient && (
                    <TrlPopoverSubMenu
                      onboardingPopoverAnchor={onboardingPopoverAnchor}
                      handleClosePopover={handleCloseOnboardingPopover}
                      popoverWidth={
                        onboardingButtonRef.current &&
                        `${onboardingButtonRef.current.offsetWidth}px`
                      }
                      subMenuOptions={legalStatusList}
                      handleOptionClick={(option) => {
                        handleCloseOnboardingPopover();
                        navigateToPage({
                          route: '/Onboarding',
                          menuItem: 'ONBOARDING',
                        });
                        dispatch(
                          onboardingSlice.actions.setLegalStatus({
                            legalStatus: option.classifierValue,
                          }),
                        );
                      }}
                    />
                  )}
                </OnboardingButtonContainer>
              )}
              <ButtonMain
                id="loginButton"
                onClick={() => {
                  navigateToPage({ route: '/Login', menuItem: 'LOGIN' });
                }}
                expressionId={LOGIN_EXP}
              />
            </>
          )}
          {showTermsAndConditions && <GeneralConditions />}
        </AppBarRightStyled>
      </AppBarWrapperStyled>
    </AppBarOuterWrapperStyled>
  );
};
